import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { sparxsEditorPanelTabSelected } from "@/features/sparxsEditor/store/domainActions";
import {
  DEFAULT_EDITOR_PANEL_SLUG,
  editorPanelSlugFromUnknown,
  type EditorPanelSlug,
} from "@/features/sparxsEditor/lib/editorUrl";
import type { RootState } from "@/store/app/storeTypes";

export type ListSort = "alphabetical" | "editedFirst";

export type TextEditorMode = "wysiwyg" | "markdown";

export interface SessionPreferencesState {
  listSort: ListSort;
  textEditorMode: TextEditorMode;
  preferredEditorPanelSlug: EditorPanelSlug;
}

const initialState: SessionPreferencesState = {
  listSort: "editedFirst",
  textEditorMode: "wysiwyg",
  preferredEditorPanelSlug: DEFAULT_EDITOR_PANEL_SLUG,
};

const sessionPreferencesSlice = createSlice({
  name: "sessionPreferences",
  initialState,
  reducers: {
    setListSort: (state, action: PayloadAction<ListSort>) => {
      state.listSort = action.payload;
    },
    setTextEditorMode: (state, action: PayloadAction<TextEditorMode>) => {
      state.textEditorMode = action.payload;
    },
    setPreferredEditorPanelSlug: (
      state,
      action: PayloadAction<EditorPanelSlug>,
    ) => {
      state.preferredEditorPanelSlug = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sparxsEditorPanelTabSelected, (state, action) => {
      state.preferredEditorPanelSlug = action.payload.preferredPanelSlug;
    });
  },
});

export const { setListSort, setTextEditorMode, setPreferredEditorPanelSlug } =
  sessionPreferencesSlice.actions;
export default sessionPreferencesSlice.reducer;

export const selectListSort = (state: RootState): ListSort =>
  state.editor.sessionPreferences?.listSort ?? "alphabetical";

export const selectTextEditorMode = (state: RootState): TextEditorMode =>
  state.editor.sessionPreferences?.textEditorMode ?? "wysiwyg";

export const selectPreferredEditorPanelSlug = (
  state: RootState,
): EditorPanelSlug => {
  const slug = state.editor.sessionPreferences?.preferredEditorPanelSlug;
  return editorPanelSlugFromUnknown(slug) ?? DEFAULT_EDITOR_PANEL_SLUG;
};
